import { readFileAsBase64 } from "@/pages/home/VehicleSituation";
import { Modal } from "flowbite-react";
import { useRef, useState } from "react";
import { HiCamera, HiOutlineCloudUpload } from "react-icons/hi";
import LoadingOverlay from "@/common/components/ui/loading/LoadingOverlay";
import { INVENTORY_SITUATION } from "@/types/inventory.types";

import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { useAppStore } from "@/common/states/store";

type ImageCaptureProps = {
  onImageReady: (files: { base64: string; fileName: string }[]) => void;
  sucessProcessImages?: boolean;
  submitInventory: (status: INVENTORY_SITUATION) => void;
  failedImages: { base64: string; fileName: string }[];
};

export const ImageCapture = ({
  onImageReady,
  submitInventory,
  failedImages,
}: ImageCaptureProps) => {
  const { notifyError } = useToastContext();

  const [isProcessingLocal, setIsProcessingLocal] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const {
    inventories: {
      modals: { isImageCaptureOpen },
      closeImageCaptureModal,
      isConvertingImagesToText,
      sucessProcessImages,
      setSucessProcessImages,
    },
  } = useAppStore();

  const isProcessing = isProcessingLocal || isConvertingImagesToText;

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const files = Array.from(event.target.files || []);

    if (files.length === 0) {
      console.error("No files selected");
      return;
    }

    setIsProcessingLocal(true);
    const filesBase64Data: { base64: string; fileName: string }[] = [];
    const localFailedFiles: string[] = [];

    try {
      const promises = files.map((file) =>
        readFileAsBase64(file).then((base64) => ({
          fileName: file.name,
          base64,
        })),
      );

      const results = await Promise.allSettled(promises);

      results.forEach((result) => {
        if (
          result.status === "fulfilled" &&
          result.value &&
          result.value.base64 &&
          result.value.fileName
        ) {
          filesBase64Data.push({
            base64: result.value.base64,
            fileName: result.value.fileName,
          });
        } else {
          const fileName =
            (result.status === "rejected" &&
              (result.reason as Error).message.split(" ").pop()) ||
            "unknown file";
          localFailedFiles.push(fileName);
          notifyError(`Falha ao processar a imagem: ${fileName}`);
        }
      });

      if (filesBase64Data.length > 0) {
        onImageReady(filesBase64Data);
      }
    } catch (error) {
      console.error("Error handling file conversion:", error);
      notifyError("Erro ao converter arquivo para base64");
    } finally {
      setIsProcessingLocal(false);
    }
  };

  const handleClose = () => {
    closeImageCaptureModal();
    setSucessProcessImages(false);
  };

  return (
    <Modal show={isImageCaptureOpen} onClose={handleClose}>
      <Modal.Header>
        {sucessProcessImages
          ? "Processamento concluído"
          : "Capturar/Carregar Imagem"}
      </Modal.Header>
      <Modal.Body>
        {sucessProcessImages ? (
          <>
            {failedImages.length > 0 && (
              <div className="border border-red-200 rounded-lg">
                <div className="bg-red-100 p-3 border-b border-red-200">
                  <h4 className="text-red-800 font-semibold text-sm">
                    {failedImages.length} imagem(ns) com falha - Não serão
                    contabilizadas no estoque
                  </h4>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <ul className="p-2 space-y-2">
                    {failedImages.map(({ base64, fileName }, index) => (
                      <li
                        key={index}
                        className="border border-red-300 rounded-lg p-3 bg-red-50"
                      >
                        <div className="flex items-center gap-3">
                          <img
                            src={`data:image/jpeg;base64,${base64}`}
                            alt={fileName}
                            className="w-16 h-16 object-cover rounded border border-red-200 flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-red-700 font-medium text-sm truncate">
                              {fileName}
                            </p>
                            <p className="text-red-500 text-xs">
                              Falha no processamento
                            </p>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            <div className="flex justify-around">
              <button
                onClick={() => {
                  handleClose();
                  submitInventory(INVENTORY_SITUATION.DONE);
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Finalizar inventário - Sim
              </button>
              <button
                onClick={() => {
                  handleClose();
                  submitInventory(INVENTORY_SITUATION.IN_PROGRESS);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Finalizar inventário - Não
              </button>
            </div>
          </>
        ) : isProcessing ? (
          <>
            <LoadingOverlay />
            <div>Processando imagens. Aguarde...</div>
          </>
        ) : (
          <div className="flex justify-around">
            {/* Carregar da galeria */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center p-4 border rounded-lg hover:bg-gray-100"
            >
              <HiOutlineCloudUpload className="w-12 h-12 text-gray-500" />
              <span className="mt-2 text-lg">Carregar Imagem</span>
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
              accept="image/*"
              multiple
            />

            {/* Tirar foto com câmera nativa */}
            <button
              onClick={() => cameraInputRef.current?.click()}
              className="flex flex-col items-center p-4 border rounded-lg hover:bg-gray-100"
              disabled={isProcessing}
            >
              <HiCamera className="w-12 h-12 text-gray-500" />
              <span className="mt-2 text-lg">Tirar Foto</span>
            </button>
            <input
              type="file"
              ref={cameraInputRef}
              onChange={handleFileChange}
              className="hidden"
              accept="image/*"
              capture="environment"
            />
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};
